<template>
  <div class="timeline-chart" />
</template>

<script>
import * as d3 from 'd3';

import * as am4core from '@amcharts/amcharts4/core';
import * as am4charts from '@amcharts/amcharts4/charts';
import * as am4pluginsTimeline from '@amcharts/amcharts4/plugins/timeline';
import am4themesAnimated from '@amcharts/amcharts4/themes/animated';

am4core.addLicense('CH245905005');
am4core.addLicense('TL245443410');
am4core.useTheme(am4themesAnimated);

export default {
  name: 'TimelineChart',
  props: {
    selectCategory: String,
    value: {
      type: Object,
      required: true,
    },
  },
  watch: {
    selectCategory(newVal) {
      this.filterByCategory(newVal);
    },
    value: {
      handler(newVal) {
        this.zoomToYear(newVal.yearStart, newVal.yearEnd);
        this.filterByRegion(newVal.region);
      },
      deep: true,
    },
  },
  mounted() {
    // load data
    const dataSource = new am4core.DataSource();
    dataSource.url = `${process.env.BASE_URL}data/timeline.csv`;
    dataSource.parser = new am4core.CSVParser();
    dataSource.parser.options.emptyAs = '';
    dataSource.parser.options.useColumnNames = true;

    dataSource.load();

    // data loaded and parsed
    dataSource.events.on('done', ({ data }) => {
      // prepare data
      this.sanitizedData = this.formulateData(data);

      // create chart
      this.chart = this.createChart();

      // create axes
      const { dateAxis, categoryAxis } = this.createChartAxes();
      this.dateAxis = dateAxis;
      this.categoryAxis = categoryAxis;

      // create series
      this.series = [];
      this.sanitizedData.forEach((d) => {
        const series = this.createSeries(d);

        this.series.push(series);
      });

      // edit grid path
      this.dateAxis.renderer.grid.template.adapter.add('path', this.editGridPath);

      // set date axis start range
      this.chart.events.on('ready', () => {
        this.zoomToYear(2020, 2020);
      });
    });
  },
  beforeDestroy() {
    if (this.chart) {
      this.chart.dispose();
    }
  },
  methods: {
    formulateData(data) {
      // group by news category, set color
      const eventByNews = d3.group(data, (d) => d.category);
      // set colors by new category
      // convert nested groupedRegion Map to Object
      /* format to => {
          key: news category,
          color: #colorString,
          regions: {
            regionName: dataItems,
          },
        }
      */
      eventByNews.forEach((value, key) => {
        let color = '';

        switch (key) {
          case 'Digital banking license':
            color = '#FDC4D0';
            break;

          case 'FinTech conferences':
            color = '#A295E2';
            break;

          case 'Funding and investments':
            color = '#005EB8';
            break;

          case 'Industry news':
            color = '#F6C76A';
            break;

          case 'Partnerships':
            color = '#E25A5F';
            break;

          default:
            break;
        }

        value.map((d) => Object.assign(d, { color }));
      });

      // .map(({ key, color, value: region }) => {
      //   const nestedRegions = Array.from(region).reduce(
      //     (acc, [name, value]) => Object.assign(acc, { [name]: value }),
      //     {}
      //   );

      //   return { key, color, regions: nestedRegions };
      // });

      let eventByRegions = d3.group(data, (d) => d['country/region 1']);
      // convert Map to Array
      eventByRegions = Array.from(eventByRegions, ([key, value]) => ({ key, value }));
      // ensure no empty 'news category'
      // return formatData.filter((d) => d.key !== '');
      return eventByRegions;
    },
    createChart() {
      // create serpentine chart
      const chart = am4core.create(this.$el, am4pluginsTimeline.SerpentineChart);
      // set serpentine options
      chart.orientation = 'vertical';
      chart.levelCount = 5;
      chart.yAxisRadius = am4core.percent(5);
      // curve options
      chart.maskBullets = false;

      // chart settings
      // disable blue highlight
      chart.focusFilter = undefined;

      // date format
      chart.dateFormatter.dateFormat = 'dd-MM-yyyy';
      chart.dateFormatter.inputDateFormat = 'dd-MM-yyyy';

      // disable zoom out button
      chart.zoomOutButton.disabled = true;
      chart.padding(30, 50, 30, 50);

      if (this.$breakpoint.mobile) {
        chart.padding(10, 10, 20, 10);
      }
      return chart;
    },
    createChartAxes() {
      const minYear = 2016;
      const maxYear = 2020;

      // x-axis - date axis
      const dateAxis = this.chart.xAxes.push(new am4charts.DateAxis());
      dateAxis.min = new Date(minYear, 0, 1).getTime();
      dateAxis.max = new Date(maxYear, 11, 31).getTime();
      dateAxis.baseInterval = { timeUnit: 'day', count: 1 };
      dateAxis.rangeChangeDuration = 500;
      dateAxis.renderer.line.strokeDasharray = '1,4';
      dateAxis.renderer.line.stroke = '#A9A8A9';
      dateAxis.renderer.grid.template.stroke = '#A9A8A9';
      dateAxis.renderer.labels.template.location = 0.0001;

      // y-axis - category axis (pseudo-axis to simulate 1 linear line)
      const categoryAxis = this.chart.yAxes.push(new am4charts.CategoryAxis());
      categoryAxis.dataFields.category = 'type';
      categoryAxis.renderer.grid.template.disabled = true;

      return { dateAxis, categoryAxis };
    },
    createSeries(data) {
      const series = this.chart.series.push(new am4pluginsTimeline.CurveLineSeries());
      // attach data
      series.data = data.value;
      // series settings
      series.name = data.key;
      series.dataFields.dateX = 'date';
      series.dataFields.categoryY = 'type';
      series.strokeOpacity = 0;

      // tooltip settings
      series.tooltip.background.cornerRadius = 8;
      series.tooltip.background.strokeOpacity = 0;
      series.tooltip.pointerOrientation = 'vertical';
      series.tooltip.animationDuration = 200;
      series.tooltip.defaultState.transitionDuration = 200;
      series.tooltip.hiddenState.transitionDuration = 200;
      series.tooltip.label.maxWidth = 320;
      series.tooltip.label.wrap = true;

      // bullet settings
      const bullet = series.bullets.push(new am4charts.CircleBullet());
      bullet.propertyFields.fill = 'color';
      bullet.stroke = '#ffffff';
      bullet.cursorOverStyle = am4core.MouseCursorStyle.pointer;

      if (this.$breakpoint.mobile) {
        bullet.circle.radius = 8;
      }

      //   bullet.tooltipText = `
      //   [font-size: 16px line-height: 1.35]{dateX.formatDate('d MMM yyyy')}[/] \n
      //   [bold font-size: 22px line-height: 1.2]{title}[/] \n
      //   [font-size: 16px line-height: 1.35]Country/region: {country/region} \n[/]
      //   [font-size: 16px line-height: 1.35]News category: {news category}[/] \n
      // `;

      // use 'over' events, because adapter will result dataItem undefined
      bullet.events.on('over', ({ target }) => {
        target.stroke = '#121212';
        target.strokeWidth = 1.5;

        const bulletData = target.dataItem.dataContext;

        // check if there are 2 regions
        if (bulletData['country/region 2'] === '') {
          bullet.tooltipHTML = `
            <div class="series-tooltip">
              <p class="series-tooltip__date text-legend">{display date}</p>

              <h5 class="series-tooltip__title font-weight-bold" data-title="${bulletData.title}">{title}</h5>

              <div class="series-tooltip__meta">
                <div>
                  <i class="series-tooltip__icon fas fa-map-marker-alt"></i>
                  <span class="text-legend">{country/region 1}</span>
                </div>

                <div class="text-legend">Category: {category}</div>
              </div>
            </div>
          `;
        } else {
          bullet.tooltipHTML = `
            <div class="series-tooltip">
              <p class="series-tooltip__date text-legend">{display date}</p>

              <h5 class="series-tooltip__title font-weight-bold" data-title="${bulletData.title}">{title}</h5>

              <div class="series-tooltip__meta">
                <div>
                  <i class="series-tooltip__icon fas fa-map-marker-alt"></i>
                  <span class="text-legend">{country/region 1}, {country/region 2}</span>
                </div>

                <div class="text-legend">Category: {category}</div>
              </div>
            </div>
          `;
        }
      });

      bullet.events.on('out', ({ target }) => {
        target.stroke = '#ffffff';
      });

      return series;
    },
    editGridPath(value) {
      // eg of path d - "M-3.7496,40.8281  L-3.7496,-8.1656" 48.9937 ~ 49
      const moveTo = value.split('L')[0].split(',');
      const lineTo = value.split('L')[1].split(',');

      // get path coordinates
      let moveToCoordX = parseFloat(moveTo[0].split('M')[1]);
      let lineToCoordX = parseFloat(lineTo[0]);
      let moveToCoordY = parseFloat(moveTo[1]);
      let lineToCoordY = parseFloat(lineTo[1]);

      // new path length - total unit = 4
      // check if path is on Turning level of chart
      if (moveToCoordX !== lineToCoordX && lineToCoordX - moveToCoordX > 2) {
        // shorten X coordinate
        moveToCoordX = lineToCoordX - 7;
        lineToCoordX -= 3;

        // check if path is on Upper or Lower side of curve
        // shorten Y coordinate
        if (moveToCoordY > lineToCoordY) {
          moveToCoordY = lineToCoordY + 7;
          lineToCoordY += 3;
        } else {
          moveToCoordY = lineToCoordY - 7;
          lineToCoordY -= 3;
        }
      } else {
        // check if path is on Odd or Even level of chart
        moveToCoordY = moveToCoordY > lineToCoordY ? moveToCoordY - 38 : moveToCoordY + 38;
        lineToCoordY = moveToCoordY > lineToCoordY ? lineToCoordY + 6 : lineToCoordY - 6;
      }

      // construct path
      const path = `M${moveToCoordX},${moveToCoordY} L${lineToCoordX},${lineToCoordY}`;

      return path;
    },
    zoomToYear(yearStart, yearEnd) {
      // update chart zoom according to v-model: yearStart/End
      const startYear = new Date(yearStart, 0, 1);
      const endYear = new Date(yearEnd, 12, 0);

      this.dateAxis.zoomToDates(startYear, endYear);
    },
    filterByRegion(region) {
      // reset - hides the chart instantly and then shows it
      this.series.forEach((series) => series.appear());

      // update series visibility according to v-model: region
      if (region !== 'All regions') {
        this.series.forEach((series) => {
          if (series.name === region) {
            series.show();
          } else {
            series.hide();
          }
        });
      }
    },
    filterByCategory(category) {
      if (category === 'All') {
        // loop through series
        this.series.forEach((set) => {
          // loop through bullets
          set.bullets.each((bullet) => {
            bullet.clones.each((clone) => {
              clone.fillOpacity = 1;
              clone.strokeOpacity = 1;
              clone.interactionsEnabled = true;
            });
          });
        });
      } else {
        // loop through series
        this.series.forEach((set) => {
          // loop through bullets
          set.bullets.each((bullet) => {
            bullet.clones.each((clone) => {
              const data = clone.dataItem.dataContext;

              // toggle bullet opacity and interactivity
              if (data.category !== category) {
                clone.fillOpacity = 0.1;
                clone.strokeOpacity = 0.1;
                clone.interactionsEnabled = false;
              } else {
                clone.fillOpacity = 1;
                clone.strokeOpacity = 1;
                clone.interactionsEnabled = true;
              }
            });
          });
        });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.timeline-chart {
  width: 100%;
  height: 720px;

  &__tooltip {
    padding: 15px;

    .tooltip__date {
      margin-bottom: 5px;
    }

    .tooltip__title {
      font-size: 22px;
      line-height: 1.2;
      margin-bottom: 15px;
    }
  }
}
</style>

<style lang="scss">
.series-tooltip {
  // tooltip default size - top 7, bottom 4, left and right 12
  // total to 15px
  padding-left: 3px;
  padding-right: 3px;
  padding-top: 8px;
  padding-bottom: 13px;
  font-family: 'Noto Sans Regular', sans-serif;
  min-width: 280px;

  .series-tooltip__date {
    margin-bottom: 5px;
  }

  .series-tooltip__title {
    font-size: 18px;
    line-height: 1.2;
    margin-bottom: 15px;
  }

  .series-tooltip__icon {
    margin-right: 5px;
  }

  // hard fix - tooltip text wrapping
  [data-title='UOB launches TMRW in Thailand'],
  [data-title='Finovate Asia Digital 2020'] {
    font-size: 18px;
  }
}
</style>
